import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Star, ArrowRight } from "@phosphor-icons/react";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "Free",
    tagline: "Walk-in customers",
    features: [
      "Access to all 100+ services at branch",
      "WhatsApp support",
      "Basic application tracking",
      "Cash / UPI payment",
    ],
    cta: "Visit Branch",
    href: "/branches",
    highlighted: false,
  },
  {
    id: "premium",
    name: "Premium",
    price: "₹499",
    priceNote: "/service",
    tagline: "For individuals",
    features: [
      "Door-step document pickup",
      "Priority processing (same day)",
      "Dedicated WhatsApp agent",
      "SMS/Email status updates",
      "Free document scanning & storage",
      "3 minor revisions included",
    ],
    cta: "Apply Online",
    href: "/apply",
    highlighted: true,
  },
  {
    id: "business",
    name: "Business",
    price: "Custom",
    tagline: "For franchisees / partners",
    features: [
      "White-label DigitalSetu ERP",
      "Multi-branch dashboard",
      "AI Draft Assistant included",
      "POS + WhatsApp + Analytics",
      "Encrypted backups + Audit logs",
      "Priority phone support",
    ],
    cta: "Talk to Sales",
    href: "/contact",
    highlighted: false,
  },
];

export default function PublicPricing() {
  return (
    <div className="max-w-7xl mx-auto px-4 py-14" data-testid="public-pricing">
      <div className="text-center mb-14 max-w-2xl mx-auto">
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#1e3a8a] mb-1.5">Pricing</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Simple, transparent pricing.</h1>
        <p className="text-slate-600 mt-2">Pay per service — no hidden fees. Government fees + a clear service charge, always shown upfront.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PLANS.map((p) => (
          <div
            key={p.id}
            data-testid={`plan-${p.id}`}
            className={`relative rounded-2xl p-8 border transition-all ${p.highlighted
              ? "bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] text-white border-[#1e3a8a] shadow-2xl shadow-blue-950/20 -translate-y-2"
              : "bg-white text-slate-900 border-slate-200"}`}
          >
            {p.highlighted && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-orange-500 text-white text-[10px] uppercase tracking-widest font-bold">
                <Star size={10} weight="fill" /> Most popular
              </div>
            )}
            <div className={`text-[10px] uppercase tracking-widest font-semibold mb-2 ${p.highlighted ? "text-orange-300" : "text-[#1e3a8a]"}`}>{p.tagline}</div>
            <div className="text-2xl font-bold mb-4" style={{ fontFamily: "var(--font-heading)" }}>{p.name}</div>
            <div className="flex items-baseline gap-1 mb-6">
              <span className="text-4xl font-bold">{p.price}</span>
              {p.priceNote && <span className={`text-sm ${p.highlighted ? "text-blue-200" : "text-slate-500"}`}>{p.priceNote}</span>}
            </div>
            <ul className="space-y-2.5 mb-8">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <CheckCircle size={16} weight="fill" className={p.highlighted ? "text-emerald-300 shrink-0 mt-0.5" : "text-emerald-500 shrink-0 mt-0.5"} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to={p.href}
              className={`w-full inline-flex items-center justify-center gap-1.5 px-4 h-11 rounded-full text-sm font-semibold ${p.highlighted ? "bg-white text-[#1e3a8a] hover:bg-slate-100" : "bg-[#1e3a8a] text-white hover:bg-[#1e2f6b]"} transition-colors`}
              data-testid={`plan-cta-${p.id}`}
            >
              {p.cta} <ArrowRight size={12} />
            </Link>
          </div>
        ))}
      </div>

      <div className="mt-16 rounded-2xl bg-slate-50 border border-slate-200 p-8 text-center">
        <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "var(--font-heading)" }}>Need a custom quote?</h3>
        <p className="text-slate-600 text-sm mb-4">Franchise partnerships, bulk services, and enterprise integrations — talk to our team.</p>
        <Link to="/contact" className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-[#ea580c] hover:bg-orange-700 text-white text-sm font-semibold">
          Talk to Sales <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}
